/**
 * Statutory identifiers for the operating entity behind AXOIX-iBOS.
 *
 * AXOIX Technologies owns iBOS, so this is the entity that contracts, invoices
 * and carries liability under the terms on this site — which is why the full
 * credential set belongs here and not only on axoix.in (the company's own
 * marketing site, run by the mothership lane).
 *
 * ── Where the values come from ───────────────────────────────────────────────
 * NOT from a copy kept here. `./statutoryIdentifiers.generated.ts` is written
 * at prebuild by scripts/sync-statutory.js from the mothership's authoritative
 * record at api.axoix.in/api/public/company-settings. Two hand-maintained
 * copies of one fact drift; a generated one can only go stale, and its header
 * stamps when it was fetched so staleness is visible.
 *
 * Build time, not runtime: this site is prerendered to static HTML, so a
 * runtime fetch would leave the legal pages, the footer and the Corporation
 * JSON-LD without identifiers in the HTML a crawler actually reads.
 *
 * GSTIN is the exception — that endpoint does not serve `legal.gstin`, so it
 * stays local until the mothership adds it, and it is the one value below a
 * human still maintains.
 *
 * Before 2026-08-15 all of these were hardcoded in eleven places.
 *
 * ── Why DPIIT is nullable ────────────────────────────────────────────────────
 * DPIIT Startup Recognition is the only identifier here that can stop being
 * true without anyone touching code: the certificate is void if turnover in any
 * financial year exceeds INR 200 Cr, and it expires on its own date regardless.
 * A stale DPIIT claim on a legal page is a false statutory statement, not a
 * cosmetic bug.
 *
 * So: set `dpiit` to null and the claim disappears from every surface at once —
 * the footer badge, the footer identifier row, all five legal pages, the terms
 * modal and the FAQ. One edit, not eleven.
 *
 * The two static files that cannot import this module — public/index.html
 * (JSON-LD) and public/llms.txt — are covered by scripts/check-statutory.js,
 * which runs as part of prebuild and fails the build if they disagree with this
 * file. You cannot blank DPIIT here and leave it live in the structured data.
 *
 * Values verified against source documents, not retyped from a summary:
 * MCA Certificate of Incorporation and the DPIIT Certificate of Recognition.
 */

import { MOTHERSHIP_LEGAL, GENERATED_AT, GENERATED_FROM } from './statutoryIdentifiers.generated';

export interface StatutoryIdentifiers {
  legalName: string;
  cin: string;
  gstin: string;
  udyam: string;
  duns: string;
  /** null => AXOIX no longer holds DPIIT recognition; every claim is withdrawn. */
  dpiit: string | null;
  dpiitIssued: string;
  dpiitValidTo: string;
  registeredOffice: string;
  incorporatedOn: string;
}

export const STATUTORY: StatutoryIdentifiers = {
  legalName: MOTHERSHIP_LEGAL.legalName,
  cin: MOTHERSHIP_LEGAL.cin,
  // Local: the endpoint does not serve legal.gstin yet.
  gstin: "07ABFCA7413E1ZY",
  udyam: MOTHERSHIP_LEGAL.udyam,
  duns: MOTHERSHIP_LEGAL.duns,
  dpiit: MOTHERSHIP_LEGAL.dpiit,
  // Dates come from the certificate; the endpoint serves the number only.
  dpiitIssued: "1 July 2026",
  dpiitValidTo: "12 May 2036",
  // The mothership stores this without the trailing ", India".
  registeredOffice: `${MOTHERSHIP_LEGAL.registeredOffice}, India`,
  incorporatedOn: "13 May 2026",
};

/** Provenance, for anyone auditing where these numbers came from. */
export const IDENTIFIER_SOURCE = { generatedAt: GENERATED_AT, from: GENERATED_FROM };

/** Label/value pairs, with DPIIT omitted entirely when it is not held. */
export const identifierPairs = (): Array<[string, string]> => {
  const pairs: Array<[string, string]> = [
    ["CIN", STATUTORY.cin],
    ["GSTIN", STATUTORY.gstin],
    ["Udyam", STATUTORY.udyam],
    ["D-U-N-S", STATUTORY.duns],
  ];
  if (STATUTORY.dpiit) pairs.push(["DPIIT", STATUTORY.dpiit]);
  return pairs;
};

/** One-line rendering, e.g. for the FAQ appendix and llms.txt-style prose. */
export const identifierLine = (separator = " · "): string =>
  identifierPairs()
    .map(([label, value]) => `${label} ${value}`)
    .join(separator);

/** Parenthetical for legal prose: "(CIN: …, GSTIN: …, …)". */
export const identifierParenthetical = (): string =>
  `(${identifierPairs()
    .map(([label, value]) => `${label}: ${value}`)
    .join(", ")})`;

/** True only while the recognition is actually held. */
export const hasDpiit = (): boolean => Boolean(STATUTORY.dpiit);
